import { Menu, Transition } from '@headlessui/react'
import { TextSearch } from '@ui'
import { Fragment, ReactNode, useMemo, useState } from 'react'
import { FaChevronDown } from 'react-icons/fa'
import { useSearchParams } from 'react-router-dom'

export const HISTORY_STATUS_OPTIONS: { label: string; value: string }[] = [
  { label: 'All Status', value: '' },
  { label: 'Active', value: 'active' },
  { label: 'Cancelled', value: 'cancelled' },
  { label: 'Resolved', value: 'resolved' }
]

type Props<TRecord> = {
  children: (records: TRecord[]) => ReactNode
  getSearchText?: (record: TRecord) => string
  getStatus?: (record: TRecord) => string | undefined
  records: TRecord[]
  searchKey: string
  title: string
}

export default function HistoryTableLayout<TRecord>({
  children,
  getSearchText = record => JSON.stringify(record),
  getStatus,
  records,
  searchKey,
  title
}: Props<TRecord>) {
  const [statusFilter, setStatusFilter] = useState('')
  const [searchParams] = useSearchParams()
  const search = (searchParams.get(searchKey) || '').toLowerCase().trim()

  const filteredRecords = useMemo(
    () =>
      records.filter(record => {
        const matchesStatus = statusFilter && getStatus ? getStatus(record)?.toLowerCase() === statusFilter : true
        const matchesSearch = search ? getSearchText(record).toLowerCase().includes(search) : true
        return matchesStatus && matchesSearch
      }),
    [getSearchText, getStatus, records, search, statusFilter]
  )

  return (
    <div className="rounded-lg border border-[#E5E7EB] px-6 py-5">
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <h2 className="text-lg font-medium text-[#111827]">
          Showing {filteredRecords.length} {title}
        </h2>

        <div className="flex flex-wrap items-center gap-2">
          <Menu as="div" className="relative">
            <Menu.Button className="flex h-10 items-center gap-2 rounded-lg border border-[#E5E7EB] px-3 text-sm text-[#374151] transition-colors hover:bg-[#F9FAFB]">
              {HISTORY_STATUS_OPTIONS.find(option => option.value === statusFilter)?.label || 'All Status'}
              <FaChevronDown className="text-[#98A2B3]" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-150"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-20 mt-2 min-w-[180px] rounded-xl border border-[#E5E7EB] bg-white p-2 shadow-lg focus:outline-none">
                {HISTORY_STATUS_OPTIONS.map(item => (
                  <Menu.Item key={item.label}>
                    {({ active }) => (
                      <button
                        className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm ${active ? 'bg-[#F9FAFB]' : ''}`}
                        onClick={() => setStatusFilter(item.value)}
                        type="button"
                      >
                        <span className="text-[#374151]">{item.label}</span>
                        {statusFilter === item.value ? <span className="text-[#111827]">✓</span> : null}
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Transition>
          </Menu>

          <div className="flex h-10 min-w-[228px] items-center gap-2">
            <TextSearch className="!w-56" searchKey={searchKey} searchPlaceholder={`Search ${title.toLowerCase()}`} />
          </div>
        </div>
      </div>

      {children(filteredRecords)}
    </div>
  )
}
